import type { PaletteModel } from '../../data/types';
import type { Vec2, Vec3 } from '../../geo/types';
import type { ChunkSink } from '../mesh/chunker';
import type { FaceMask, Frame, MeshBuilder, VertexStyle } from '../mesh/meshBuilder';
import { SURFACE, edgeIdFor } from '../mesh/attributes';
import { layoutToWorld } from '../../core/coords';
import { WORLD } from '../../core/config';
import type { Obstacle } from '../types';
import type { PlacedGoods } from './quayGoods';

export const GOODS_EDGE_BASE = 50000;

// Goods stand on the quay, so no floor is drawn under them.
const STANDING: FaceMask = { top: true, bottom: false, sides: true };

function goodsStyle(palette: PaletteModel, index: number): VertexStyle {
  return {
    color: palette.shipHull.median.linear,
    surface: SURFACE.wood,
    edgeId: edgeIdFor(GOODS_EDGE_BASE + index),
    seed: 0,
    strokeAngle: 0,
    extent: [0, 0],
  };
}

// A box in the item's frame: x along the quay, z across it, from the quay up.
function block(
  builder: MeshBuilder,
  style: VertexStyle,
  frame: Frame,
  [x0, x1]: readonly [number, number],
  [z0, z1]: readonly [number, number],
  height: number
): void {
  const min: Vec3 = [x0, 0, z0];
  const max: Vec3 = [x1, height, z1];
  builder.box(style, frame, min, max, STANDING);
}

// The frame's two long planks run the item's length; the two short ones fit between them.
function planks(item: PlacedGoods): [[number, number], [number, number]][] {
  const [l, w, bar] = [item.length / 2, item.width / 2, item.bar ?? 0];
  return [
    [
      [-l, l],
      [-w, -w + bar],
    ],
    [
      [-l, l],
      [w - bar, w],
    ],
    [
      [-l, -l + bar],
      [-w + bar, w - bar],
    ],
    [
      [l - bar, l],
      [-w + bar, w - bar],
    ],
  ];
}

// The fork's arms: how far below the beam they reach, and how thick they are.
const FORK_ARM_M = 0.3;
const FORK_ARM_THICKNESS_M = 0.08;

/* A square timber of side `size` between two points in the item's across-and-up plane (x = 0), as
   a stay runs. */
function strut(
  builder: MeshBuilder,
  style: VertexStyle,
  frame: Frame,
  [z0, y0]: readonly [number, number],
  [z1, y1]: readonly [number, number],
  size: number
): void {
  const length = Math.hypot(z1 - z0, y1 - y0);
  const [nz, ny] = [(-(y1 - y0) / length) * (size / 2), ((z1 - z0) / length) * (size / 2)];
  const h = size / 2;
  const ring = (z: number, y: number): Vec3[] => [
    [-h, y - ny, z - nz],
    [h, y - ny, z - nz],
    [h, y + ny, z + nz],
    [-h, y + ny, z + nz],
  ];
  const [a, b] = [ring(z0, y0), ring(z1, y1)];
  builder.quad(style, frame, [a[3]!, a[2]!, a[1]!, a[0]!]);
  builder.quad(style, frame, [b[0]!, b[1]!, b[2]!, b[3]!]);
  for (let k = 0; k < 4; k++) {
    const j = (k + 1) % 4;
    builder.quad(style, frame, [a[k]!, a[j]!, b[j]!, b[k]!]);
  }
}

/* The woodcut's forked trestle (plan section 6.3): a post at the bank, a level beam across the quay
   resting on it and on a forked prop, and a stay from the post's head down to the fork. */
function trestle(builder: MeshBuilder, style: VertexStyle, frame: Frame, item: PlacedGoods): void {
  const [h, w] = [item.length / 2, item.width / 2];
  const [beam, fork, side] = [item.beam ?? 0, item.fork ?? 0, item.bankSide];
  const post = side * (w - h);
  const prop = post - side * fork * (item.width - item.length);
  const under = beam - item.length;
  block(builder, style, frame, [-h, h], [post - h, post + h], item.height);
  builder.box(style, frame, [-h, under, -w], [h, beam, w]);
  block(builder, style, frame, [-h, h], [prop - h, prop + h], under);
  for (const x of [-h - FORK_ARM_THICKNESS_M, h]) {
    const arm: Vec3 = [x + FORK_ARM_THICKNESS_M, beam, prop + h];
    builder.box(style, frame, [x, under - FORK_ARM_M, prop - h], arm);
  }
  strut(
    builder,
    style,
    frame,
    [post - side * h, item.height - item.length],
    [prop, beam],
    item.length
  );
}

/** Every item as timber on its quay, into the infrastructure chunk where it stands. */
export function drawQuayGoods(
  items: readonly PlacedGoods[],
  palette: PaletteModel,
  chunks: ChunkSink,
  heightAt: (p: Vec2) => number = () => WORLD.groundY
): { readonly items: number; readonly triangles: number } {
  let triangles = 0;
  items.forEach((item, index) => {
    const builder = chunks.builderAt('infrastructure', item.centre);
    const before = builder.triangleCount;
    const style = goodsStyle(palette, index);
    const frame: Frame = { origin: layoutToWorld(item.centre, heightAt(item.centre)), yaw: item.yaw };
    if (item.kind === 'trestle') {
      trestle(builder, style, frame, item);
    } else if (item.kind === 'plankFrame') {
      for (const [along, across] of planks(item)) {
        block(builder, style, frame, along, across, item.height);
      }
    } else {
      const [l, w] = [item.length / 2, item.width / 2];
      block(builder, style, frame, [-l, l], [-w, w], item.height);
    }
    triangles += builder.triangleCount - before;
  });
  return { items: items.length, triangles };
}

/** The walker steps over a frame's planks but not round the rest. */
export function goodsObstacles(items: readonly PlacedGoods[]): Obstacle[] {
  return items
    .filter((item) => item.kind !== 'plankFrame')
    .map((item) => ({ id: `quayGoods:${item.id}`, kind: 'structure', polygon: item.footprint }));
}
